import { useState } from "react";
import { Icon, type IconName } from "@/components/common/Icon";

interface NavItemButtonProps {
	icon: IconName;
	label: string;
	/** アイコンだけを出す。ラベルは遅延なしのツールチップで補う。 */
	collapsed?: boolean;
	/** 設定ダイアログの左ナビ向けに文字とアイコンを一段小さくする。 */
	compact?: boolean;
	selected?: boolean;
	onClick: () => void;
	testId: string;
}

const HOVER_ICON_SCALE = 1.15;

/** アイコンとラベルを並べた縦ナビの項目。サイドバーと設定ダイアログの左ナビで見た目を揃える。 */
export function NavItemButton({
	icon,
	label,
	collapsed = false,
	compact = false,
	selected = false,
	onClick,
	testId,
}: NavItemButtonProps) {
	const tooltip = collapsed ? label : undefined;
	const [hovered, setHovered] = useState(false);

	const background = selected
		? "var(--overlay-active)"
		: hovered
			? "var(--overlay-hover)"
			: "transparent";
	const color = selected || hovered ? "var(--fg-0)" : "var(--fg-2)";

	return (
		<button
			type="button"
			data-testid={testId}
			onClick={onClick}
			onPointerEnter={() => setHovered(true)}
			onPointerLeave={() => setHovered(false)}
			title={tooltip}
			data-tooltip-side="right"
			data-tooltip-delay={0}
			style={{
				display: "flex",
				alignItems: "center",
				gap: compact ? 8 : 10,
				height: 32,
				flexShrink: 0,
				padding: collapsed ? 0 : "0 8px",
				justifyContent: collapsed ? "center" : "flex-start",
				border: 0,
				borderRadius: "var(--radius-3)",
				background,
				boxShadow: selected ? "var(--shadow-ring)" : "none",
				color,
				cursor: "pointer",
				fontFamily: "inherit",
				fontSize: compact ? "var(--text-caption)" : "var(--text-ui)",
				fontWeight: selected ? 600 : 500,
				letterSpacing: "var(--tracking-snug)",
				transition: "background 120ms ease, color 120ms ease",
			}}
		>
			<span
				style={{
					display: "grid",
					placeItems: "center",
					flexShrink: 0,
					transform: hovered ? `scale(${HOVER_ICON_SCALE})` : "none",
					transition: "transform var(--motion-collapse-duration) var(--motion-ease-out)",
				}}
			>
				<Icon name={icon} size={compact ? 16 : 18} color={color} />
			</span>
			{!collapsed && <span style={{ whiteSpace: "nowrap" }}>{label}</span>}
		</button>
	);
}
